import { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import api from 'api';
import { setAlert } from 'redux/alert';
import { RootState } from 'redux/store';
import type { ChatMessage } from 'types';

const useExpertReview = () => {
    const dispatch = useDispatch();
    const { id: userId } = useSelector((state: RootState) => state.user.entity);

    const [isLoading, setIsLoading] = useState(false);
    const [sendingMessage, setSendingMessage] = useState(false);
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [chatGroupId, setChatGroupId] = useState('');
    const [users, setUsers] = useState<string[]>([]);
    const [userIds, setUserIds] = useState<number[]>([]);
    const [hasAccess, setHasAccess] = useState(false);
    const [canAccessBackoffice, setCanAccessBackoffice] = useState(false);

    const resetChat = () => {
        setMessages([]);
        setChatGroupId('');
        setUsers([]);
        setUserIds([]);
        setHasAccess(false);
        setCanAccessBackoffice(false);
    };

    const getApplicantChatObj = async (applicationId: number | string, isBackOffice: boolean) => {
        resetChat();
        setIsLoading(true);

        try {
            const response = await api.chatNew.get_application_chat_messages(
                applicationId.toString(),
                isBackOffice
            );

            const data = response.data;
            const newChatGroupId = data.chat_group_id?.toString() ?? '';

            setChatGroupId(newChatGroupId);
            setMessages(data.messages || []);
            setUsers(data.users || []);
            setUserIds(data.user_ids || []);
            setCanAccessBackoffice(data.can_access_backoffice || false);

            // Check if user has access to this chat group
            if (newChatGroupId) {
                try {
                    const accessRes = await api.chatNew.has_access(parseInt(newChatGroupId));
                    setHasAccess(Boolean(accessRes.data?.has_access));
                } catch {
                    setHasAccess(false);
                }
            }
        } catch (error) {
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    };

    // Poll for new messages every 5 seconds
    useEffect(() => {
        if (!chatGroupId || !userId) return;

        let lastPolledAt = new Date().toISOString();
        let requestInFlight = false;

        const interval = setInterval(async () => {
            if (requestInFlight) {
                return;
            }

            requestInFlight = true;
            const pollStartedAt = new Date().toISOString();

            try {
                const res = await api.chatNew.poll_messages(parseInt(chatGroupId), lastPolledAt);
                const newMessages: ChatMessage[] = res.data;

                if (newMessages.length > 0) {
                    setMessages((prev) => {
                        const existingIds = new Set(prev.map((m) => m.id));
                        const uniqueNew = newMessages.filter((msg) => {
                            if (existingIds.has(msg.id)) {
                                return false;
                            }

                            existingIds.add(msg.id);
                            return true;
                        });

                        if (uniqueNew.length === 0) return prev;
                        return [...prev, ...uniqueNew];
                    });
                }
                lastPolledAt = pollStartedAt;
            } catch (error) {
                console.error('Polling error:', error);
            } finally {
                requestInFlight = false;
            }
        }, 5000);

        return () => clearInterval(interval);
    }, [chatGroupId, userId]);

    // Send message with optimistic update
    const handleSendMessage = async (text: string) => {
        if (!chatGroupId || !text.trim()) return;

        const tempId = -Date.now();
        const optimisticMessage: ChatMessage = {
            id: tempId,
            chat_group_id: parseInt(chatGroupId),
            text: text.trim(),
            user: null,
            user_id: userId,
            created_at: new Date().toISOString(),
            read: true,
        };

        // Add message to list immediately
        setMessages((prev) => [...prev, optimisticMessage]);
        setSendingMessage(true);

        try {
            const response = await api.chatNew.send_message(parseInt(chatGroupId), text.trim());

            // Replace temp ID with real ID — if poll already added the real
            // message, just drop the optimistic duplicate instead.
            const realId = response.data.message_id;
            setMessages((prev) => {
                const realIdExists = prev.some((m) => m.id === realId);
                if (realIdExists) {
                    return prev.filter((m) => m.id !== tempId);
                }
                return prev.map((m) => (m.id === tempId ? { ...m, id: realId } : m));
            });
        } catch {
            // Remove optimistic message on failure
            setMessages((prev) => prev.filter((msg) => msg.id !== tempId));
            dispatch(setAlert({ message: 'Failed to send message', type: 'error' }));
        } finally {
            setSendingMessage(false);
        }
    };

    // Dedupe by id and sort chronologically
    const sortedMessages = useMemo(() => {
        const seen = new Set<number>();
        const unique = messages.filter((m) => {
            if (seen.has(m.id)) return false;
            seen.add(m.id);
            return true;
        });
        return unique.sort(
            (a, b) => new Date(a.created_at || 0).getTime() - new Date(b.created_at || 0).getTime()
        );
    }, [messages]);

    return {
        isLoading,
        messages: sortedMessages,
        chatGroupId,
        users,
        userIds,
        hasAccess,
        canAccessBackoffice,
        sendingMessage,
        handleSendMessage,
        getApplicantChatObj,
        resetChat,
    };
};

export default useExpertReview;
